import React from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MonetizationOnIcon from '@material-ui/icons/MonetizationOn';
import GroupIcon from '@material-ui/icons/Group';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import MoneyOffIcon from '@material-ui/icons/MoneyOff';
import WarningIcon from '@material-ui/icons/Warning';
import AssessmentIcon from '@material-ui/icons/Assessment';
import ShowChartIcon from '@material-ui/icons/ShowChart';

import StatsCard from '../dashboard/shared/StatsCard';
import RefreshButton from '../dashboard/shared/RefreshButton';
import FinanceSeriesChart from './FinanceSeriesChart';

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4)
  },
  header: {
    marginBottom: theme.spacing(4),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
  titleWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
  titleIcon: {
    fontSize: 40,
    color: theme.palette.primary.main,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 320
  },
  metaCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
  },
  staleRow: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  sectionTitle: {
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },
  tablePaper: {
    overflowX: 'auto',
  },
}));

const formatWhole = (value) => new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
}).format(Number(value || 0));

const formatCurrency = (value, currency = 'EUR', digits = 2) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number(value || 0));

const formatTimestamp = (value) => {
  if (!value) return 'Unknown';
  return new Date(value).toLocaleString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const FinanceOverview = ({
  finance,
  loading,
  error,
  onRefresh,
  refreshing = false,
}) => {
  const classes = useStyles();
  const {
    overview = {},
    charts = {},
    tables = {},
    freshness,
    fx,
  } = finance || {};

  if (loading && !freshness) {
    return (
      <Container className={classes.root}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  if (error && !freshness) {
    return (
      <Container className={classes.root}>
        <Paper className={classes.metaCard}>
          <Typography variant="h6" gutterBottom>
            Error Loading Finance Dashboard
          </Typography>
          <Typography color="textSecondary">{error}</Typography>
          <Box mt={2}>
            <RefreshButton onRefresh={onRefresh} loading={refreshing} />
          </Box>
        </Paper>
      </Container>
    );
  }

  const chartLabels = (charts.revenueByMonth || []).map((entry) => entry.label);

  return (
    <Container className={classes.root} maxWidth="xl">
      <Box className={classes.header}>
        <Box className={classes.titleWrap}>
          <AssessmentIcon className={classes.titleIcon} />
          <Box>
            <Typography variant="h4" component="h1">
              Finance Dashboard
            </Typography>
            <Typography color="textSecondary">
              Business health view for memberships, revenue, and server costs
            </Typography>
          </Box>
        </Box>

        <RefreshButton onRefresh={onRefresh} loading={refreshing || loading} />
      </Box>

      <Paper className={classes.metaCard}>
        <Box className={classes.metaRow}>
          <Typography variant="body2" color="textSecondary">
            Last snapshot:
          </Typography>
          <Typography variant="body2">{formatTimestamp(freshness?.generatedAt)}</Typography>
          <Typography variant="body2" color="textSecondary">
            FX:
          </Typography>
          <Typography variant="body2">
            1 USD = {Number(fx?.rate || 0).toFixed(4)} EUR
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Source:
          </Typography>
          <Typography variant="body2">{fx?.source || 'Unknown'}</Typography>
        </Box>

        {freshness?.isStale && (
          <Box className={`${classes.metaRow} ${classes.staleRow}`}>
            <WarningIcon fontSize="small" />
            <Typography variant="body2">
              Showing the last successful snapshot because the latest refresh failed.
            </Typography>
          </Box>
        )}

        {error && (
          <Box className={`${classes.metaRow} ${classes.staleRow}`}>
            <WarningIcon fontSize="small" />
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="Total Members"
            value={formatWhole(overview.totalMembers)}
            subtitle="All users in the finance audience"
            icon={<GroupIcon />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="Paid Members"
            value={formatWhole(overview.totalPaidMembers)}
            subtitle="Self-serve revenue memberships"
            icon={<MonetizationOnIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="Complimentary"
            value={formatWhole(overview.complimentaryMembers)}
            subtitle="Admin-granted memberships"
            icon={<GroupIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="Projected Revenue"
            value={formatCurrency(overview.projectedMonthlyRevenueEur)}
            subtitle={`Velocity ${formatCurrency(overview.monthlyVelocityEurPerDay, 'EUR')} / day`}
            icon={<TrendingUpIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="Projected Server Costs"
            value={formatCurrency(overview.projectedMonthlyServerCostsEur)}
            subtitle={`${formatCurrency(overview.currentMonthServerCostsUsd, 'USD')} MTD raw`}
            icon={<MoneyOffIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4} lg={2}>
          <StatsCard
            title="Projected Net"
            value={formatCurrency(overview.projectedNetEur)}
            subtitle={`${formatWhole(overview.cancelledMemberships)} cancellations this month`}
            icon={<ShowChartIcon />}
            color={Number(overview.projectedNetEur || 0) >= 0 ? 'success' : 'error'}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" className={classes.sectionTitle}>
        Trends
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <FinanceSeriesChart
            title="Revenue, Server Costs, and Net"
            labels={chartLabels}
            datasets={[
              {
                label: 'Revenue (EUR)',
                data: (charts.revenueByMonth || []).map((entry) => entry.revenueEur),
                borderColor: '#2e7d32',
                backgroundColor: '#2e7d32',
              },
              {
                label: 'Server Costs (EUR)',
                data: (charts.serverCostsByMonth || []).map((entry) => entry.serverCostsEur),
                borderColor: '#ed6c02',
                backgroundColor: '#ed6c02',
              },
              {
                label: 'Net (EUR)',
                data: (charts.netByMonth || []).map((entry) => entry.netEur),
                borderColor: '#1976d2',
                backgroundColor: '#1976d2',
              },
            ]}
          />
        </Grid>
        <Grid item xs={12} lg={4}>
          <FinanceSeriesChart
            title="Member Mix"
            labels={chartLabels}
            datasets={[
              {
                label: 'Paid Members',
                data: (charts.paidMembersByMonth || []).map((entry) => entry.paidMembers),
                borderColor: '#2e7d32',
                backgroundColor: '#2e7d32',
              },
              {
                label: 'Complimentary Members',
                data: (charts.complimentaryMembersByMonth || []).map((entry) => entry.complimentaryMembers),
                borderColor: '#6d4c41',
                backgroundColor: '#6d4c41',
              },
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <FinanceSeriesChart
            title="Cancellations by Month"
            labels={chartLabels}
            datasets={[
              {
                label: 'Cancellations',
                data: (charts.cancellationsByMonth || []).map((entry) => entry.cancellations),
                borderColor: '#d32f2f',
                backgroundColor: '#d32f2f',
              },
            ]}
          />
        </Grid>
      </Grid>

      <Typography variant="h6" className={classes.sectionTitle}>
        Monthly Breakdown
      </Typography>
      <Paper className={classes.tablePaper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Month</TableCell>
              <TableCell align="right">Revenue (EUR)</TableCell>
              <TableCell align="right">Server Costs (EUR)</TableCell>
              <TableCell align="right">Server Costs (USD)</TableCell>
              <TableCell align="right">Net (EUR)</TableCell>
              <TableCell align="right">Paid Members</TableCell>
              <TableCell align="right">Complimentary</TableCell>
              <TableCell align="right">Cancellations</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(tables.monthlyBreakdown || []).map((row) => (
              <TableRow key={row.monthKey}>
                <TableCell>{row.label}</TableCell>
                <TableCell align="right">{formatCurrency(row.revenueEur)}</TableCell>
                <TableCell align="right">{formatCurrency(row.serverCostsEur)}</TableCell>
                <TableCell align="right">{formatCurrency(row.serverCostsUsd, 'USD')}</TableCell>
                <TableCell align="right">{formatCurrency(row.netEur)}</TableCell>
                <TableCell align="right">{formatWhole(row.paidMembers)}</TableCell>
                <TableCell align="right">{formatWhole(row.complimentaryMembers)}</TableCell>
                <TableCell align="right">{formatWhole(row.cancellations)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="h6" className={classes.sectionTitle}>
        Cancellation Reasons
      </Typography>
      <Paper className={classes.tablePaper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Reason</TableCell>
              <TableCell align="right">Count</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(tables.cancellationReasons || []).length > 0 ? (
              (tables.cancellationReasons || []).map((row) => (
                <TableRow key={row.reason}>
                  <TableCell>{row.reason}</TableCell>
                  <TableCell align="right">{formatWhole(row.count)}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  No cancellation data available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Container>
  );
};

export default FinanceOverview;
